import * as React from 'react';
import { sanitizeMoneyInput, normalizeMoneyOnBlur } from '@mlm/shared';
import { Input, type InputProps } from './input';

export type MoneyInputProps = Omit<InputProps, 'type' | 'inputMode'> & {
  /** Digits only — for quantity-like fields (stock qty). */
  integer?: boolean;
};

/**
 * Text input that can only hold a server-valid money string: no commas,
 * letters, or negatives; a single dot; at most 4 decimals. Blur normalizes
 * ".2" → "0.2" and "5." → "5". Drop-in replacement for `<Input>` on money
 * fields — the caller's onChange still receives the event, with
 * `e.target.value` already sanitized. When a keystroke is stripped the caret
 * moves to the end; that only happens on invalid input.
 */
export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  ({ integer, onChange, onBlur, ...props }, ref) => (
    <Input
      ref={ref}
      inputMode={integer ? 'numeric' : 'decimal'}
      autoComplete="off"
      onChange={(e) => {
        const clean = sanitizeMoneyInput(e.target.value, { integer });
        if (clean !== e.target.value) e.target.value = clean;
        onChange?.(e);
      }}
      onBlur={(e) => {
        const norm = normalizeMoneyOnBlur(e.target.value, { integer });
        if (norm !== e.target.value) {
          e.target.value = norm;
          onChange?.(e as unknown as React.ChangeEvent<HTMLInputElement>);
        }
        onBlur?.(e);
      }}
      {...props}
    />
  ),
);
MoneyInput.displayName = 'MoneyInput';
